import { Box, Button, CircularProgress, IconButton, Typography, useMediaQuery } from "@mui/material";
import React, { useRef, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import SelectComponent from "../../components/SelectComponent/SelectComponent";
import lang from "../../assets/lang.png";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsBackToHoreca, setLanguageID, updateDefaultLanguage,
} from "../../store/slice/Ticketing.slice";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import Time from "./Time";
import BookNowFormTicket from "./BookNowFormTicket";
import EventComponent from "./Event";
import CalendarTicket from "./CalenderTicket";
import MultipleEventCalaenderView from "./MultipleEventCalaenderView";
import EventOrStandardReservationType from "./EventOrStandardReservationType";
import InactivityPopup from "../../utils/InActivityPopup";
import LanguagePopover from "../Component/LanguagePopover";
import { WidgetHeightProvider, useWidgetHeight } from "../horecaWidget/widgetHeightContext";

// ─── inner content ─────────────────────────────────────────────────────────────
const WidgetContent = ({ handleClose, loading, widgetpos, eventStdTicketLoader, loader }) => {
  const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;
  const theme   = useSelector((store) => store?.widgetSettings?.theme); // or useTheme() if wired

  const { onPopoverOpen, onPopoverClose } = useWidgetHeight();

  const {
    totalCount, EventDates, selectedDate, reservType,
    defaultLanguage, languageWords, isOpenWidget, SelectedEventOrStdResType,
  } = useSelector((store) => store.Ticketing);

  const { key, widgetConfig, languageData, resType, widgetSetupId, deviceId, css } =
    useSelector((store) => store?.widgetSettings);

  const bookBtnCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Book button"
  )?.fields;

  const location  = useLocation();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const { isBookNowOpen } = useSelector((store) => store.addGuest);

  const hasImage       = Boolean(widgetConfig?.settings?.imageFileName?.trim());
  const widgetPosition = widgetConfig?.settings?.widgetPosition ?? "Top";

  // ── refs ──────────────────────────────────────────────────────────────────
  const eventRef        = useRef(null);
  const dateRef         = useRef(null);
  const reservTypeRef   = useRef(null);
  const timeRef         = useRef(null);

  // ── popover state ─────────────────────────────────────────────────────────
  const [isEventOpen,      setIsEventOpen]      = useState(false);
  const [isDateOpen,       setIsDateOpen]        = useState(false);
  const [isResTypeOpen,    setIsResTypeOpen]    = useState(false);
  const [isTimeOpen,       setIsTimeOpen]       = useState(false);

  // ── helpers ───────────────────────────────────────────────────────────────
  const blur = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  const closeAll = useCallback((except) => {
    if (except !== "event")   setIsEventOpen(false);
    if (except !== "date")    setIsDateOpen(false);
    if (except !== "resType") setIsResTypeOpen(false);
    if (except !== "time")    setIsTimeOpen(false);
  }, []);

  const toggle = (setter, key, isOpen) => () => {
    blur();
    if (isOpen) { setter(false); onPopoverClose(); }
    else        { closeAll(key); setter(true);  onPopoverOpen(); }
  };

  const close = (setter) => () => { blur(); setter(false); onPopoverClose(); };

  // ── language ──────────────────────────────────────────────────────────────
  const handleLanguageChange = (langCode) => {
    const selected = languageData?.find((item) => item.langcode === langCode);
    if (!selected) return;
    dispatch(setIsBackToHoreca(false));
    dispatch(updateDefaultLanguage(selected.langcode));
    dispatch(setLanguageID(selected.idlanguage));
    localStorage.setItem("widget", JSON.stringify({
      id: widgetSetupId, key, lang: selected.langcode, resType,
    }));
  };

  // ── derived ───────────────────────────────────────────────────────────────
  const getTimeValue = () => {
    if (Object.keys(reservType ?? {}).length > 0 && selectedDate) {
      if (reservType?.isStandardTicket) return reservType?.eventDateAndTime;
      return `${dayjs(selectedDate?.fromTime, "HH:mm:ss").format("HH:mm")} - ${dayjs(selectedDate?.toTime, "HH:mm:ss").format("HH:mm")}`;
    }
    return "HH:MM";
  };

  const isBookActive =
    reservType && typeof reservType === "object" &&
    Object.keys(reservType).length > 0 &&
    totalCount > 0 && selectedDate;

  const commonSelectSx = {
    width: "100%",
    height: "72px",
    WebkitTapHighlightColor: "transparent",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme?.widget?.inputBorderColor}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme?.widget?.inputBorderColor}`,
    },
    "& .MuiOutlinedInput-input": {
      padding: 1,
      color: theme?.guest?.textColor,
      ml: 3, mt: 1,
      fontSize: { xs: "12px", md: "14px" },
    },
  };

  const imageBgSx = hasImage
    ? {
        backgroundImage: `url(${IMG_URL}/upload/Widgetsetup${widgetConfig.settings.imageFileName.replace(/\\/g, "/")})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        minHeight: widgetConfig?.settings?.imageHeight
          ? `${widgetConfig.settings.imageHeight}px`
          : "100vh",
      }
    : { backgroundColor: theme?.widget?.mainBg };

  const panelSx = hasImage
    ? {
        position: "absolute", left: 0, right: 0, zIndex: 1,
        ...(widgetPosition === "Top"    && { top: 0 }),
        ...(widgetPosition === "Bottom" && { bottom: 0 }),
        ...(widgetPosition === "Middle" && { top: "50%", transform: "translateY(-50%)" }),
      }
    : { position: "relative" };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        overflowX: "hidden",
        ...imageBgSx,
      }}
    >
      {deviceId && <InactivityPopup languageWords={languageWords} />}

      <Box sx={{ ...panelSx, width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3 },
          }}
        >
          {/* Language + Close */}
          {languageData?.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <LanguagePopover
                  langIcon={lang}
                  languageData={languageData}
                  selectedLanguage={defaultLanguage}
                  onChange={handleLanguageChange}
                  theme={theme}
                  defaultLanguage={defaultLanguage}
                />
                {widgetConfig?.settings?.floatButtonActive &&
                  !widgetConfig?.settings?.kioskMode && (
                  <IconButton
                    size="large"
                    onClick={handleClose}
                    sx={{
                      backgroundColor: theme?.widgetButton?.closeBg,
                      minWidth: { xs: 44, sm: "auto" },
                      minHeight: { xs: 44, sm: "auto" },
                      "&:hover": { backgroundColor: theme?.widgetButton?.closeHover },
                    }}
                  >
                    <CloseIcon sx={{ color: theme?.widgetButton?.closeText }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {totalCount >= widgetConfig?.reservationRequestMin && (
            <Box sx={{ color: theme?.widget?.reservGrpTextClr, mb: 1, fontSize: { xs: "13px", sm: "14px" } }}>
              {languageWords?.reservationGroupText}
            </Box>
          )}

          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
              color: theme?.widget?.titleTextClr,
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {languageWords?.widgetHeading || "Start uw reservering"}
          </Typography>

          <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch" sx={{ position: "relative" }}>

            {/* Event */}
            <Grid item xs={12} sm={6} md={4} lg={2.5}>
              <SelectComponent
                label="Event"
                value={reservType?.eventTicketName || "Ticket"}
                onClick={toggle(setIsEventOpen, "event", isEventOpen)}
                ref={eventRef}
                sx={commonSelectSx}
              />
              <EventComponent
                isOpen={isEventOpen}
                handleClose={close(setIsEventOpen)}
                Ref={eventRef.current}
                loading={loader}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6} md={3} lg="auto">
              <SelectComponent
                label={languageWords?.date}
                value={
                  Object.keys(reservType ?? {}).length > 0
                    ? reservType?.isStandardTicket
                      ? selectedDate && dayjs(selectedDate).isValid()
                        ? dayjs(selectedDate).format("DD-MM-YYYY")
                        : ""
                      : selectedDate?.availableDate ?? ""
                    : ""
                }
                onClick={toggle(setIsDateOpen, "date", isDateOpen)}
                ref={dateRef}
                disabled={
                  Object.keys(reservType ?? {}).length === 0 ||
                  (!reservType?.isStandardTicket && EventDates?.length === 1)
                }
                sx={commonSelectSx}
              />
              {reservType?.isStandardTicket ? (
                <CalendarTicket
                  isOpen={isDateOpen}
                  handleClose={close(setIsDateOpen)}
                  Ref={dateRef.current}
                  setOpenNext={setIsResTypeOpen}
                />
              ) : (
                <MultipleEventCalaenderView
                  isOpen={isDateOpen}
                  handleClose={close(setIsDateOpen)}
                  Ref={dateRef.current}
                  setOpenNext={setIsResTypeOpen}
                />
              )}
            </Grid>

            {/* Reservation Type */}
            <Grid item xs={12} sm={12} md="auto" lg="auto" sx={{ minWidth: 220 }}>
              <SelectComponent
                label={languageWords?.reserveType}
                value={
                  SelectedEventOrStdResType &&
                  typeof SelectedEventOrStdResType === "object" &&
                  Object.keys(SelectedEventOrStdResType).length > 0
                    ? SelectedEventOrStdResType?.name
                    : "Type"
                }
                onClick={toggle(setIsResTypeOpen, "resType", isResTypeOpen)}
                ref={reservTypeRef}
                // disabled={!selectedDate?.availableDate?.trim()}
                disabled={
                 selectedDate?.availableDate?.trim() != "" ? false : true
                                      }
                sx={commonSelectSx}
              />
              <EventOrStandardReservationType
                isOpen={isResTypeOpen}
                handleClose={close(setIsResTypeOpen)}
                setOpen={setIsResTypeOpen}
                Ref={reservTypeRef.current}
                eventStdTicketLoader={eventStdTicketLoader}
              />
            </Grid>

            {/* Time (read-only display) */}
            <Grid item xs={12} sm={12} md={3} lg={3} sx={{ minWidth: 180 }}>
              <SelectComponent
                label={languageWords?.Time}
                value={getTimeValue()}
                onClick={toggle(setIsTimeOpen, "time", isTimeOpen)}
                ref={timeRef}
                disabled={true}
                sx={{ ...commonSelectSx, width: "fit-content", minWidth: "140px" }}
              />
              <Time
                isOpen={isTimeOpen}
                handleClose={close(setIsTimeOpen)}
                Ref={timeRef.current}
              />
            </Grid>

            {/* Book Now */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Button
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  height: "72px",
                  backgroundColor: isBookActive
                    ? bookBtnCss?.BookNowButtonBackgroundColorActive
                    : bookBtnCss?.BookNowButtonBackgroundColor,
                  color: isBookActive
                    ? bookBtnCss?.BookNowButtonTextColorActive
                    : bookBtnCss?.BookNowButtonTextColor,
                  fontWeight: "bold",
                  fontSize: { xs: "13px", sm: "14px" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "none",
                  borderRadius: "8px",
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                  "&:hover": { boxShadow: "none" },
                  "&:active": { transform: "scale(0.98)" },
                }}
                onClick={() => {
                  if (isBookActive && !loading) navigate("/bookNowTickets");
                }}
              >
                {loading
                  ? <CircularProgress sx={{ color: "white" }} size={24} />
                  : languageWords?.Booknow
                }
                {reservType?.totalCost > 0 && totalCount < widgetConfig?.reservationRequestMin && (
                  <Box component="span" fontSize="13px" fontWeight="200">
                    {parseFloat(reservType?.totalCost).toFixed(2)}
                  </Box>
                )}
              </Button>
            </Grid>

          </Grid>

          {isBookNowOpen && <BookNowFormTicket />}
        </Box>
      </Box>
    </Box>
  );
};

// ─── public wrapper ────────────────────────────────────────────────────────────
const Widget = ({ handleClose, loading, widgetpos, heightChange, eventStdTicketLoader, loader }) => {
  const hasImage = Boolean(
    useSelector((s) => s?.widgetSettings?.widgetConfig?.settings?.imageFileName)?.trim()
  );
  return (
    <WidgetHeightProvider onHeightChange={heightChange} disabled={hasImage}>
      <WidgetContent
        handleClose={handleClose}
        loading={loading}
        widgetpos={widgetpos}
        eventStdTicketLoader={eventStdTicketLoader}
        loader={loader}
      />
    </WidgetHeightProvider>
  );
};

export default Widget;
